import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import type { Scope } from '../types';
import { parseProjectPathSegments } from './parseProjectPathSegments';

/**
 * Derive the agent's current working scope from the URL.
 *
 * URL is the canonical scope carrier; everything downstream (composer chip,
 * outgoing socket payloads, agent context filter) reads from this hook.
 *
 * Priority order when multiple artifact segments are present in the URL
 * (e.g. `/projects/p1/cohort/c1/variable/v1`):
 *   variable → concept_group → dataset → cohort → project.
 * Variable wins over its parent cohort because users land at "view the
 * variable" — the surrounding cohort is breadcrumb context only.
 *
 * The returned Scope is referentially stable across renders when the URL
 * hasn't changed (memoized on path identity), so consumers that diff
 * scope-by-reference (e.g. the `scope_changed` emitter) won't false-fire.
 */
export const useCurrentScope = (projectId: string): Scope => {
  const { pathname } = useLocation();

  return useMemo<Scope>(() => {
    if (!projectId) {
      // Defensive: not strictly reachable since `useCurrentScope` is only
      // called inside the project page where `projectId` is required.
      return { kind: 'project', projectId: '' };
    }

    const segments = parseProjectPathSegments(pathname, projectId);

    if (segments.variableId) {
      return { kind: 'variable', projectId, variableId: segments.variableId };
    }
    if (segments.conceptGroupId) {
      return { kind: 'concept_group', projectId, conceptGroupId: segments.conceptGroupId };
    }
    if (segments.datasetId) {
      return { kind: 'dataset', projectId, datasetId: segments.datasetId };
    }
    if (segments.cohortId) {
      return { kind: 'cohort', projectId, cohortId: segments.cohortId };
    }
    return { kind: 'project', projectId };
  }, [pathname, projectId]);
};
